import '@ant-design/compatible/assets/index.css';
import isEqual from 'lodash/isEqual';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import { Input, Tooltip } from 'antd';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React from 'react';
import TableColumnSearch from '~components/table-column-search';
import CurrencyBar from '~components/currency-bar';
import SelectPropertyInfo from 'common/components/property-type-select';
import SelectBookingType from 'common/components/booking-type-select';
import { bookingJourneyType } from 'common/constants/booking';

@withTranslation()
class BasicInformation extends React.Component {
  constructor() {
    super();

    this.autoInputValue = {
      cityValue: '',
      landlordValue: '',
    };

    this.state = {};
  }

  componentDidMount() {
    if (this.props.basicInfo && Object.keys(this.props.basicInfo).length > 0) {
      this.props.form.setFieldsValue({
        type: this.props.basicInfo.type,
        name: this.props.basicInfo.name,
        city: this.props.basicInfo.city.slug,
        landlord: this.props.basicInfo.landlord.name,
        bookingJourney: this.props.basicInfo.bookingJourney,
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.basicInfo, nextProps.basicInfo)) {
      if (nextProps.basicInfo.landlord) {
        this.props.form.setFieldsValue({
          landlord: nextProps.basicInfo.landlord.name,
        });
      }
      if (nextProps.basicInfo.bookingJourney !== this.props.basicInfo.bookingJourney) {
        this.props.form.setFieldsValue({
          bookingJourney: nextProps.basicInfo.bookingJourney,
        });
      }
    }
  }

  handleSearch = (slug, value, type = true) => {
    if (type === 'landlord') {
      this.props.form.setFieldsValue({
        landlord: value.name,
        bookingJourney: value.bookingJourney ?? undefined,
      });
      this.props.setPropertyData(
        'basicInfo',
        {
          landlord: value,
        },
        true,
      );
    } else {
      this.props.form.setFieldsValue({
        city: slug || '',
      });
      this.props.setPropertyData(
        'basicInfo',
        {
          city: value,
        },
        true,
      );
    }
  };

  changeCurrencyState = (state, data) => {
    this.props.setCountryData(state, data);
    this.props.setPropertyData('basicInfo', {
      countryData: data,
    });
  };

  resetValue = searchType => {
    if (searchType === 'city') {
      this.props.form.resetFields(['city']);
    } else {
      this.props.form.resetFields(['landlord']);
    }
  };

  saveSearchValue = (value, searchType) => {
    this.props.changeLeaveAlert();
    if (searchType === 'city') {
      this.autoInputValue.cityValue = value;
    } else {
      this.autoInputValue.landlordValue = value;
    }
  };

  typeSelect = (type, propertyTypeData) => {
    let index = propertyTypeData.findIndex(property => property?.slug === type);
    const apartmentName = propertyTypeData[index]?.name;
    this.props.handlePropertyType(type, apartmentName);
    this.props.changeLeaveAlert();
  };

  handleBookingJourney = bookingJourney => {
    this.props.setPropertyData('basicInfo', {
      bookingJourney,
    });
  };

  render() {
    const { t, basicInfo, countryData } = this.props;
    const { getFieldDecorator, getFieldValue } = this.props.form;

    return (
      <div className={'basic-info'}>
        <Form className="basic-info__form">
          <Form.Item>
            <span className="basic-info__label">
              {t('before_start_tell_us_what_is_your_apartment_type')}
            </span>
            {getFieldDecorator('type', {
              rules: [
                {
                  required: true,
                  message: t('select_your_apartment_type'),
                },
              ],
              validateTrigger: 'onBlur',
            })(<SelectPropertyInfo onChange={this.typeSelect} />)}
          </Form.Item>
          <Form.Item>
            <span className="basic-info__label">
              {t('cms.properties.create.basic_information.apartment_name_label')}
            </span>
            {getFieldDecorator('name', {
              rules: [
                {
                  required: true,
                  message: t('cms.properties.edit.error.blank'),
                },
                {
                  validator: (rule, value, callback) => {
                    const emojiRegex = new RegExp(
                      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
                    );
                    if (value && emojiRegex.test(value)) {
                      callback('error');
                    }
                    callback();
                  },
                  message: t('cms.properties.create.create_confirmation.error_emoji'),
                },
                {
                  validator: (rule, value, callback) => {
                    if (value && value.length > 250) {
                      callback('error');
                    }
                    callback();
                  },
                  message: t('cms.properties.create.create_confirmation.error_length'),
                },
              ],
              validateTrigger: 'onBlur',
            })(
              <Input
                placeholder={t(
                  'cms.properties.create.basic_information.apartment_name_placeholder',
                )}
                onChange={this.props.changeLeaveAlert}
              />,
            )}
          </Form.Item>
          <Form.Item>
            <div
              className="basic-info__city"
              ref={node => {
                this.basicInfoCity = node;
              }}
            >
              <span className="basic-info__label">
                {t('cms.properties.create.basic_information.apartment_city_label')}
                <Tooltip
                  title={t('cms.properties.create.basic_information.apartment_city_tooltip')}
                  placement="right"
                >
                  <ExclamationCircleOutlined className="basic-info__circle" />
                </Tooltip>
              </span>
              {getFieldDecorator('city', {
                rules: [
                  {
                    validator: (rule, value, callback) => {
                      if (this.autoInputValue.cityValue && !getFieldValue('city')) {
                        callback('error');
                      }
                      callback();
                    },
                    message: t('cms.properties.create.basic_information.apartment_city_invalid'),
                  },
                  {
                    validator: (rule, value, callback) => {
                      if (!getFieldValue('city')) {
                        callback('error');
                      }
                      callback();
                    },
                    message: t('cms.properties.edit.error.blank'),
                  },
                ],
                trigger: 'onChange',
              })(
                <TableColumnSearch
                  searchType="city"
                  publishedStatus="PUBLISHED"
                  isLocationCustom
                  showCountry
                  onSearch={(slug, value, type) => {
                    this.handleSearch(slug, value, type);
                  }}
                  t={t}
                  changeCurrencyState={this.changeCurrencyState}
                  placeholder={t(
                    'cms.properties.create.basic_information.apartment_city_placeholder',
                  )}
                  basicType="basicCity"
                  valueData={
                    basicInfo.city ? `${basicInfo.city.name}, ${basicInfo.city.countryName}` : ''
                  }
                  resetValue={this.resetValue}
                  saveSearchValue={this.saveSearchValue}
                  parentNode={this.basicInfoCity}
                />,
              )}
              <CurrencyBar t={t} countryData={countryData.data ? countryData.data : null} />
            </div>
          </Form.Item>
          <Form.Item>
            <div className="filter">
              <div
                className="basic-info__landlord"
                ref={node => {
                  this.basicInfoLandlord = node;
                }}
              >
                <span className="basic-info__label">
                  {' '}
                  {t('cms.properties.create.basic_information.apartment_landlord_label')}
                  <Tooltip
                    title={t('cms.properties.create.basic_information.apartment_landlord_tooltip')}
                    placement="right"
                  >
                    <ExclamationCircleOutlined className="basic-info__circle" />
                  </Tooltip>
                </span>
                {getFieldDecorator('landlord', {
                  rules: [
                    {
                      validator: (rule, value, callback) => {
                        if (this.autoInputValue.landlordValue && !getFieldValue('landlord')) {
                          callback('error');
                        }
                        callback();
                      },
                      message: t(
                        'cms.properties.create.basic_information.apartment_landlord_invalid',
                      ),
                    },
                    {
                      validator: (rule, value, callback) => {
                        if (!getFieldValue('landlord')) {
                          callback('error');
                        }
                        callback();
                      },
                      message: t('cms.properties.edit.error.blank'),
                    },
                  ],
                  trigger: 'onChange',
                })(
                  <TableColumnSearch
                    searchType="landlord"
                    isLocationCustom
                    onSearch={(slug, value, type) => {
                      this.handleSearch(slug, value, type);
                    }}
                    t={t}
                    placeholder={t(
                      'cms.properties.create.basic_information.apartment_landlord_placeholder',
                    )}
                    valueData={basicInfo.landlord ? basicInfo.landlord.name : ''}
                    resetValue={this.resetValue}
                    saveSearchValue={this.saveSearchValue}
                    parentNode={this.basicInfoLandlord}
                  />,
                )}
              </div>
            </div>
          </Form.Item>
          <Form.Item>
            <div
              className="basic-info__bookingType"
              ref={node => (this.basicInfoBookingType = node)}
            >
              <span className="basic-info__label">
                {t('cms.properties.create.basic_information.apartment_booking_type_label')}
              </span>
              {getFieldDecorator('bookingJourney', {
                initialValue: basicInfo.landlord?.bookingJourney,
                rules: [
                  {
                    required: true,
                    message: t('select_your_booking_type'),
                  },
                ],
                validateTrigger: 'onBlur',
              })(
                <SelectBookingType
                  onChange={this.handleBookingJourney}
                  ignoreOption={bookingJourneyType.MANUAL}
                />,
              )}
            </div>
          </Form.Item>
        </Form>
      </div>
    );
  }
}

BasicInformation.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  handlePropertyType: PropTypes.func.isRequired,
  basicInfo: PropTypes.object.isRequired,
  setPropertyData: PropTypes.func.isRequired,
  setCountryData: PropTypes.func.isRequired,
  countryData: PropTypes.object.isRequired,
  changeLeaveAlert: PropTypes.func.isRequired,
};

BasicInformation.defaultProps = {
  t: () => {},
};

const BasicInformationForm = Form.create({
  name: 'basic_information_form',
  onFieldsChange: (props, changedFields) => {
    Object.keys(changedFields).map(key => {
      if (!changedFields[key].errors) {
        if (
          key === 'name' &&
          props.basicInfo &&
          props.basicInfo.name !== changedFields[key].value
        ) {
          props.setPropertyData(
            'basicInfo',
            {
              [key]: changedFields[key].value,
            },
            true,
          );
        }
      }
      return true;
    });
  },
})(BasicInformation);

export default BasicInformationForm;
